import React from "react";
import {
  Box,
  Dialog,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MicIcon from "@mui/icons-material/Mic";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";

import { getMicTroubleshoot } from "../utils/micTroubleshoot";

/**
 * Troubleshoot microphone (mobile) — the slimmed mobile counterpart of the
 * desktop `dialogs/TroubleshootDialog`.
 *
 * The desktop version is a 740×600 two-pane dialog with Microphone / Device /
 * Questions tabs whose guidance (address-bar lock icon, `ms-settings:sound`,
 * "use a desktop device") is written for desktop browsers. On mobile we keep
 * ONLY the Microphone section and rewrite the steps for how permissions are
 * actually changed in a mobile browser (iOS Safari / Android Chrome).
 *
 * @param {boolean}  open
 * @param {()=>void} onClose
 * @param {boolean}  granted   current mic-permission status (from the parent)
 */
export default function TroubleshootMicModal({
  open = false,
  onClose = () => {},
  granted = false,
}) {
  const { browserName, steps, fallback } = React.useMemo(getMicTroubleshoot, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, m: 3 } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 4,
          py: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <MicIcon fontSize="small" sx={{ color: "primary.clr-300" }} />
          <Typography variant="h6-semiBold" color="neutral.clr-900">
            Troubleshoot microphone
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ color: "neutral.clr-800", p: 1 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      {/* Content */}
      <Box sx={{ px: 4, py: 4, display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Permission status */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {granted ? (
            <CheckCircleOutlinedIcon
              fontSize="small"
              sx={{ color: "success.clr-700" }}
            />
          ) : (
            <ReportProblemOutlinedIcon
              fontSize="small"
              sx={{ color: "danger.clr-700" }}
            />
          )}
          <Typography
            variant="body01-semiBold"
            color={granted ? "success.clr-700" : "danger.clr-700"}
          >
            {granted
              ? "Microphone permission is granted"
              : "Microphone permission is not granted"}
          </Typography>
        </Box>

        {granted ? (
          <Typography variant="body02-regular" color="neutral.clr-700">
            Your microphone is ready. Speak and watch the level bar move — if it
            doesn&apos;t, check that your mic isn&apos;t muted and that no other
            app is using it.
          </Typography>
        ) : (
          <>
            <Typography variant="body02-regular" color="neutral.clr-700">
              Allow microphone access to continue
              {browserName ? ` in ${browserName}` : ""}:
            </Typography>

            <Box
              component="ol"
              sx={{
                pl: 5,
                m: 0,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {steps.map((step, i) => (
                <Typography
                  key={i}
                  component="li"
                  variant="body02-regular"
                  color="neutral.clr-700"
                >
                  {step}
                </Typography>
              ))}
            </Box>

            {fallback && (
              <Typography variant="body02-regular" color="neutral.clr-500">
                {fallback}
              </Typography>
            )}

            <Box
              sx={{
                border: "1px solid",
                borderColor: "primary.clr-200",
                bgcolor: "primary.clr-50",
                borderRadius: 2,
                p: 3,
              }}
            >
              <Typography variant="body02-semiBold" color="primary.clr-400">
                After changing the setting, reload this page for it to take
                effect.
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
}

